'use client';

// base
import React, { useMemo, useRef, useState } from 'react';

// styles
import { Column, Row } from 'styles';

// components
import { BasicLabel, BasicSelect, BasicTextField, SingleDatepicker, TextButton } from 'components';

// domains
import { useFetchNations } from 'domains/Setting';
import { ReqCreateUser, useCreateUser } from 'domains/Admin';

// contexts
import { useAlert } from 'contexts';

// libraries
import dayjs from 'dayjs';
import { stringToDate } from 'utils';

interface UserCreateProps {
  onClose: () => void;
}

export const UserCreate: React.FC<UserCreateProps> = ({ onClose }) => {
  const [createUserRequest, setCreateUserRequest] = useState<ReqCreateUser>({
    firstName: '',
    lastName: '',
    password: '1234',
    mobile: '',
    nationId: '',
    mobileNationId: '',
    email: '',
    birthDate: '',
    bloodType: '',
    iqamaNo: '',
    iqamaExpiryDate: '',
    file: undefined
  });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { showAlert, showConfirmAlert } = useAlert();

  const { data: nations } = useFetchNations({ data: { mobile: false } });
  const { data: mobileNations } = useFetchNations({ data: { mobile: true } });
  const { mutateAsync: createUser } = useCreateUser();

  const handleCreateUser = () => {
    showConfirmAlert('Are you sure you want to create this user?', () => {
      createUser(
        { data: createUserRequest },
        {
          onSuccess: (data) => showAlert(data.message, onClose),
          onError: (error) => showAlert('Failed to create user')
        }
      );
    });
  };

  return (
    <Column>
      <Row>
        <Column fullWidth>
          <Column gap="9px">
            <BasicLabel label={'Picture'} />
            <div
              style={{
                height: '350px',
                background: '#DADADA',
                cursor: 'pointer',
                borderRadius: '6px',
                border: '1px solid #DADADA'
              }}
              onClick={() => {
                inputRef.current?.click();
              }}
            >
              <input
                type="file"
                accept="image/*"
                ref={inputRef}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setCreateUserRequest((prev) => ({
                      ...prev,
                      file
                    }));
                  }
                }}
              />
              {createUserRequest.file && (
                <img
                  src={URL.createObjectURL(createUserRequest.file)}
                  alt="preview"
                  style={{ width: '100%', height: '100%', objectFit: 'fill', borderRadius: '6px' }}
                />
              )}
            </div>
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Company'} required />
            <BasicTextField disabled fullWidth value={'Samsung E&A'} />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'ID (Mobile)'} required />
            <Row>
              <BasicSelect
                style={{ width: '174px' }}
                options={(mobileNations || []).map((nation) => ({
                  label: (
                    <Row style={{ alignItems: 'center' }}>
                      <img style={{ width: '20px', height: '20px' }} src={nation.flagUri} />
                      <Row gap="0px">
                        <div>{nation.countryCode}</div>
                        <div>{nation.numberCode}</div>
                      </Row>
                    </Row>
                  ),
                  value: nation.nationId
                }))}
                value={createUserRequest.mobileNationId}
                onChange={(e) => {
                  setCreateUserRequest((prev) => ({
                    ...prev,
                    mobileNationId: e.target.value as string
                  }));
                }}
              />
              <BasicTextField
                style={{ width: '174px' }}
                placeholder="Mobile"
                value={createUserRequest.mobile}
                onChange={(e) => {
                  setCreateUserRequest((prev) => ({
                    ...prev,
                    mobile: e.target.value
                  }));
                }}
              />
            </Row>
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Name(English)'} required />
            <Row>
              <BasicTextField
                fullWidth
                placeholder="First Name"
                value={createUserRequest.firstName}
                onChange={(e) => {
                  setCreateUserRequest((prev) => ({
                    ...prev,
                    firstName: e.target.value
                  }));
                }}
              />
              <BasicTextField
                fullWidth
                placeholder="Last Name"
                value={createUserRequest.lastName}
                onChange={(e) => {
                  setCreateUserRequest((prev) => ({
                    ...prev,
                    lastName: e.target.value
                  }));
                }}
              />
            </Row>
          </Column>
        </Column>

        <Column fullWidth>
          <Column gap="9px">
            <BasicLabel label={'E-mail'} />
            <BasicTextField
              placeholder="E-mail"
              value={createUserRequest.email}
              onChange={(e) => {
                setCreateUserRequest((prev) => ({
                  ...prev,
                  email: e.target.value
                }));
              }}
            />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Birth Date'} required />
            <SingleDatepicker
              fullWidth
              onChange={(date) => {
                if (date) {
                  setCreateUserRequest((prev) => ({
                    ...prev,
                    birthDate: dayjs(date).format('YYYY-MM-DD')
                  }));
                }
              }}
              initialDate={stringToDate(createUserRequest.birthDate)}
            />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Nationality'} required />
            <BasicSelect
              options={(nations || []).map((nation) => ({
                label: (
                  <Row style={{ alignItems: 'center' }}>
                    <img style={{ width: '20px', height: '20px' }} src={nation.flagUri} />
                    <div>{nation.countryName}</div>
                  </Row>
                ),
                value: nation.nationId
              }))}
              onChange={(e) => {
                setCreateUserRequest((prev) => ({
                  ...prev,
                  nationId: e.target.value as string
                }));
              }}
              value={createUserRequest.nationId}
            />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Blood Type'} required />
            <BasicSelect
              options={[
                { label: 'A+', value: 'A+' },
                { label: 'A-', value: 'A-' },
                { label: 'B+', value: 'B+' },
                { label: 'B-', value: 'B-' },
                { label: 'AB+', value: 'AB+' },
                { label: 'AB-', value: 'AB-' },
                { label: 'O+', value: 'O+' },
                { label: 'O-', value: 'O-' }
              ]}
              onChange={(e) => {
                setCreateUserRequest((prev) => ({
                  ...prev,
                  bloodType: e.target.value as string
                }));
              }}
              value={createUserRequest.bloodType}
            />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Iqama No.'} required />
            <BasicTextField
              placeholder="Iqama No"
              value={createUserRequest.iqamaNo}
              onChange={(e) => {
                setCreateUserRequest((prev) => ({
                  ...prev,
                  iqamaNo: e.target.value
                }));
              }}
            />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Iqama Expiry Date'} required />
            <SingleDatepicker
              fullWidth
              initialDate={stringToDate(createUserRequest.iqamaExpiryDate)}
              onChange={(date) => {
                if (date) {
                  setCreateUserRequest((prev) => ({
                    ...prev,
                    iqamaExpiryDate: dayjs(date).format('YYYY-MM-DD')
                  }));
                }
              }}
            />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'Authority'} required />
            <BasicTextField disabled />
          </Column>

          <Column gap="9px">
            <BasicLabel label={'User Status'} required />
            <BasicTextField disabled />
          </Column>
        </Column>
      </Row>

      <Row style={{ justifyContent: 'end' }}>
        <TextButton variant="contained" label={'Save'} onClick={handleCreateUser} />
      </Row>
    </Column>
  );
};
